import { AppointmentModel } from "app/services/api/types/calendar/models/AppointmentModel";
import LoadingSpinner from "ui/atoms/spinner/LoadingSpinner";
import { View } from "ui/atoms/view";
import { Pressable, ScrollView, Text } from "react-native";
import { useMemo, useState } from "react";
import { getFullName } from "ui/utils/nameUtil";
import { format, isSameDay } from "date-fns";
import { EventViewHeader } from "../organisms/EventViewHeader";
import { Button, ButtonText } from "ui/gluestack/button";
import { useCompleteAppointmentMutation } from "app/services/api/calendarApi";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { CheckIcon, Icon } from "ui/gluestack/icon";
import { AppointmentStatusEnums } from "app/services/api/types/calendar/enums/AppointmentStatusEnums";
import { PermissionCheck } from "app/features/permission/atoms/PermissionCheck";
import { PermissionsEnums } from "app/services/api/types/authorisation/enums/PermissionsEnums";
import { useCalendarEventTypes } from "../hooks/useCalendarEventTypes";

interface Props {
  appointment?: AppointmentModel;
  occurrence?: {
    isComplete?: boolean;
    start?: Date | null;
    end?: Date | null;
  } | null;
  appointmentStatus?: string;
  onClose: () => void;
  onEdit: () => void;
  onCancel: () => void;
}

export function CalendarEventDetailView({
  appointment,
  occurrence,
  appointmentStatus,
  onClose,
  onEdit,
  onCancel,
}: Props) {
  const [completeAppointment, { isLoading: isLoadingComplete }] =
    useCompleteAppointmentMutation();
  const { showErrorToast, showNewToast } = useDisplayToast();
  const [isComplete, setIsComplete] = useState(occurrence?.isComplete);
  const { friendlyNames, isLoading: areEventsLoading } =
    useCalendarEventTypes();

  const residents = useMemo(() => {
    if (!appointment) return "";
    if (appointment.all_residents) {
      return "All residents";
    }
    if (appointment.participants) {
      const participants = appointment.participants
        .slice(0, 4)
        .map((participant) => getFullName(participant))
        .join(", ");

      if (appointment.participants.length > 4) {
        return `${participants} and ${appointment.participants.length - 4} more`;
      }
      return participants;
    }
  }, [appointment?.all_residents, appointment?.participants]);

  const startDate = occurrence?.start ?? appointment?.starts_at ?? null;
  const endDate = occurrence?.end ?? appointment?.ends_at ?? null;

  const sameStartEnd = useMemo(() => {
    return appointment && isSameDay(appointment.starts_at, appointment.ends_at);
  }, [appointment]);

  const transportRequired = useMemo(
    () =>
      appointment?.additional_info.find(
        (info) => info.key === "transport_required",
      )?.value === "true"
        ? "Yes"
        : "No",
    [appointment?.additional_info],
  );
  const transportBooked = useMemo(
    () =>
      appointment?.additional_info.find(
        (info) => info.key === "transport_booked",
      )?.value === "true"
        ? "Yes"
        : "No",
    [appointment?.additional_info],
  );

  const roles = useMemo(
    () => appointment?.roles.map((role) => role.display_name).join(", ") ?? "-",
    [appointment?.roles],
  );

  const handleComplete = async () => {
    try {
      if (!appointment) {
        throw new Error("No appointment found.");
      }

      if (!occurrence?.start && !appointment?.starts_at) {
        throw new Error("Unable to complete. Missing occurrence start date");
      }
      setIsComplete(true);

      await completeAppointment({
        id: appointment.id,
        body: {
          occurrence_date: format(
            occurrence?.start || appointment.starts_at,
            "yyyy-MM-dd",
          ),
        },
      }).unwrap();

      showNewToast({
        title: "Appointment complete",
        message: "The appointment has been completed successfully.",
        type: "success",
      });
    } catch (e) {
      showErrorToast(e);
      setIsComplete(false);
    }
  };

  const hideCancelButton = useMemo(() => {
    return (
      appointmentStatus === AppointmentStatusEnums.CANCELLED ||
      appointmentStatus === AppointmentStatusEnums.COMPLETED
    );
  }, [appointment?.status]);

  const isLoading = isLoadingComplete || areEventsLoading;

  if (!appointment) {
    return <LoadingSpinner />;
  }
  return (
    <View className="flex-1">
      <EventViewHeader
        appointment={appointment}
        occurrenceDate={occurrence?.start}
        onClose={onClose}
        onEdit={onEdit}
      />
      <ScrollView showsVerticalScrollIndicator={false}>
        <PermissionCheck permission={PermissionsEnums.COMPLETE_CALENDAR_EVENT}>
          {occurrence?.start || appointment?.starts_at ? (
            <View className="grid grid-cols-[200px_1fr] gap-4 py-7 border-b border-border-200">
              <Text className="text-typography-500">Mark as done</Text>
              <View className="flex-row gap-3 items-center">
                <Pressable
                  disabled={isLoading || isComplete}
                  onPress={handleComplete}
                  className={`rounded-full w-5 h-5 border border-background-300  items-center justify-center ${isComplete ? "opacity-50 bg-background-500" : "border-background-300"}`}
                >
                  {isComplete ? (
                    <Icon
                      as={CheckIcon}
                      className="w-3 h-3 stroke-white stroke-4"
                    />
                  ) : null}
                </Pressable>
              </View>
            </View>
          ) : null}
        </PermissionCheck>
        <View className="grid grid-cols-[200px_1fr] gap-4 py-7 border-b border-border-200">
          <Text className="text-typography-500">Residents</Text>
          <Text>{residents}</Text>
          <Text className="text-typography-500">Appointment Type</Text>
          <Text>{friendlyNames[appointment.type]}</Text>
          {appointment.type_specification ? (
            <>
              <View></View>
              <View>
                <Text className="text-typography-500 mb-2">Note</Text>
                <Text>{appointment.type_specification}</Text>
              </View>
            </>
          ) : null}
          <Text className="text-typography-500">Location Type</Text>
          <Text>{appointment.location_type}</Text>
          <Text className="text-typography-500">Location Description</Text>
          <Text>{appointment.location_description || "-"}</Text>

          {sameStartEnd ? (
            <>
              <Text className="text-typography-500">Date</Text>
              <Text>{startDate ? format(startDate, "dd/MM/yyyy") : null}</Text>
              <Text className="text-typography-500">Time</Text>
              <Text>
                {startDate ? format(startDate, "HH:mm") : null} -{" "}
                {endDate ? format(endDate, "HH:mm") : null}
              </Text>
            </>
          ) : (
            <>
              <Text className="text-typography-500">Start Date & Time</Text>
              <Text>
                {startDate ? format(startDate, "dd/MM/yyyy HH:mm") : null}
              </Text>
              <Text className="text-typography-500">End Date & Time</Text>
              <Text>
                {endDate ? format(endDate, "dd/MM/yyyy HH:mm") : null}
              </Text>
            </>
          )}
        </View>
        <View className="grid grid-cols-[200px_1fr] gap-4 py-7 border-b border-border-200">
          <Text className="text-typography-500">Transport Required</Text>
          <Text>{transportRequired}</Text>

          {transportRequired === "Yes" ? (
            <>
              <Text className="text-typography-500">Transport Booked</Text>
              <Text>{transportBooked}</Text>
            </>
          ) : null}
          <Text className="text-typography-500">Roles</Text>
          <Text>{roles}</Text>
        </View>
        <View className="grid grid-cols-[200px_1fr] gap-4 py-7 border-b border-border-200">
          <Text className="text-typography-500">Repeat</Text>
          <Text className="capitalize">
            {!appointment.is_recurring
              ? "never"
              : appointment.recurrence_rule?.frequency}
          </Text>
          {appointment.is_recurring ? (
            <>
              <Text className="text-typography-500">Ends</Text>
              <Text>
                {appointment.recurrence_rule?.end_date
                  ? format(
                      appointment.recurrence_rule.end_date,
                      `'on' dd/MM/yyyy`,
                    )
                  : appointment.recurrence_rule?.max_occurrences
                    ? `after ${appointment.recurrence_rule.max_occurrences} times`
                    : "never"}
              </Text>
            </>
          ) : null}
        </View>
        <View className="py-7 border-b border-border-200">
          <Text className="font-semibold mb-2">Note</Text>
          <Text>{appointment.notes || "No note"}</Text>
        </View>
        <PermissionCheck permission={PermissionsEnums.CANCEL_CALENDAR_EVENT}>
          {hideCancelButton ? null : (
            <View className="py-7 items-start">
              <Button variant="outline" action="negative" onPress={onCancel}>
                <ButtonText>Cancel Appointment</ButtonText>
              </Button>
            </View>
          )}
        </PermissionCheck>
      </ScrollView>
    </View>
  );
}
